import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';
import { useParams } from 'react-router-dom';
import { useAPI } from '../contexts/APIContext';
import LoadingScreen from '../components/LoadingScreen';
import { setShareMeta } from '../utils/shareMeta';
import { useShareLinkOpened } from '../utils/telemetryDeck';

const ShareCharacter = () => {
  const { id } = useParams();
  useShareLinkOpened("character", id);
  // Share token from the link (?token=…) — required by the gated share endpoint.
  const shareToken = new URLSearchParams(window.location.search).get('token');
  const { apiClient } = useAPI();
  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAnimated, setShowAnimated] = useState(true);

  useEffect(() => {
    // Safari Smart App Banner: aim OPEN at this exact content. The base
    // app-id meta ships in index.html; here we add the per-page deep link.
    const meta = document.querySelector('meta[name="apple-itunes-app"]');
    if (meta) {
      meta.setAttribute(
        'content',
        `app-id=6751121342, app-argument=${window.location.href}`
      );
    }
  }, []);

  useEffect(() => {
    const loadSharedCharacter = async () => {
      if (!apiClient || !id) return;

      try {
        setLoading(true);
        setError(null);
        logger.log('🔗 Loading shared character:', id);
        
        const response = await apiClient.getSharedCharacter(id, shareToken);
        logger.log('📊 Shared character response:', response);

        if (response.status && response.data) {
          setCharacter(response.data);
          setShareMeta({
            title: `Meet ${response.data.name} — made with Pixley`,
            description: `Say hello to ${response.data.name}! Created in Pixley, where kids bring their own characters and stories to life.`,
            image: response.data.animated_image_url || response.data.image_url,
          });
        } else {
          setError('Character not found or not available for sharing');
        }
      } catch (error) {
        logger.error('Failed to load shared character:', error);
        setError('Failed to load character');
      } finally {
        setLoading(false);
      }
    };

    loadSharedCharacter();
  }, [id, apiClient]);

  if (loading) {
    return <LoadingScreen message="Loading character..." type="character" />;
  }

  if (error || !character) {
  return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/50 to-pink-900/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-32">
        <div className="text-center py-16">
            <div className="text-6xl mb-6">😞</div>
            <h1 className="text-3xl font-bold text-white mb-4">Character Not Found</h1>
            <p className="text-gray-300 mb-8">
              {error || "Sorry, we couldn't find this character."}
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-medium transition-all duration-200"
          >
            Go to Pixley
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="character-detail-container min-h-screen">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-blue-400/20 to-cyan-400/20 rounded-full blur-lg animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 rounded-full blur-md animate-pulse animation-delay-4000"></div>
      </div>

      {/* Hero Section */}
      <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/50 to-pink-900/30">
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

        {/* Character Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="flex flex-col lg:flex-row items-end gap-12 lg:gap-16 min-h-[70vh]">
            {/* Character Avatar */}
            <div className="flex-shrink-0 w-full lg:w-80 xl:w-96">
              <div className="relative aspect-[3/4] lg:aspect-[2/3] xl:aspect-[3/4] max-w-sm mx-auto lg:mx-0 bg-gradient-to-br from-white/10 to-white/5 rounded-3xl overflow-hidden border border-white/20 shadow-2xl backdrop-blur-xl">
                {character.animated_image_url || character.image_url || character.handdrawn_image_url ? (
                  <img
                    src={
                      showAnimated && character.animated_image_url
                        ? character.animated_image_url
                        : character.handdrawn_image_url || character.image_url || '/assets/handdrawn-character.png'
                    }
                    alt={character.name}
                    className="w-full h-full object-cover transition-opacity duration-300"
                    onError={(e) => {
                      e.target.src = character.handdrawn_image_url || '/assets/handdrawn-character.png';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="text-6xl mb-4">🎭</div>
                      <p className="text-xl font-medium">{character.name}</p>
                    </div>
                  </div>
                )}

                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-t from-purple-500/20 via-transparent to-transparent opacity-60"></div>

                {/* Animation Toggle Controls */}
                {character.animated_image_url && character.handdrawn_image_url && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                    <div className="flex gap-2 p-1 bg-black/50 backdrop-blur-md rounded-2xl border border-white/20">
                      <button
                        onClick={() => setShowAnimated(false)}
                        className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 text-sm ${
                          !showAnimated
                            ? 'bg-blue-500/80 text-white shadow-lg'
                            : 'text-white/70 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Handdrawn
                      </button>
                      <button
                        onClick={() => setShowAnimated(true)}
                        className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 text-sm ${
                          showAnimated
                            ? 'bg-purple-500/80 text-white shadow-lg'
                            : 'text-white/70 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Animated
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Character Info */}
            <div className="flex-1 text-center lg:text-left lg:pb-8">
              <div className="mb-6">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-none tracking-tight mb-6">
                  {character.name}
                </h1>

                {/* Character Traits & Meta */}
                <div className="space-y-6 mb-12">
                  {/* Primary Meta Tags */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                    <span className="px-5 py-2.5 bg-gradient-to-r from-white/20 to-white/10 backdrop-blur-md text-white rounded-full text-sm font-semibold border border-white/20 shadow-lg">
                      Character
                    </span>
                    {character.age && (
                      <span className="px-5 py-2.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-md text-white rounded-full text-sm font-semibold border border-white/20 shadow-lg">
                        Age {character.age}
                      </span>
                    )}
                    {character.personality && (
                      <span className="px-5 py-2.5 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-md text-white rounded-full text-sm font-semibold border border-white/20 shadow-lg">
                        {character.personality.split(' ')[0]}
                      </span>
                    )}
                  </div>

                  {/* Detailed Traits */}
                  {(character.personality || character.voice_description || character.backstory) && (
                    <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                      {character.personality && (
                        <div className="px-6 py-3 bg-gradient-to-r from-white/15 to-white/5 backdrop-blur-xl rounded-2xl border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300">
                          <span className="text-white text-base font-medium">✨ {character.personality}</span>
                        </div>
                      )}
                      {character.voice_description && (
                        <div className="px-6 py-3 bg-gradient-to-r from-white/15 to-white/5 backdrop-blur-xl rounded-2xl border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300">
                          <span className="text-white text-base font-medium">🎵 {character.voice_description}</span>
                        </div>
                      )}
                      {character.backstory && (
                        <div className="px-6 py-3 bg-gradient-to-r from-white/15 to-white/5 backdrop-blur-xl rounded-2xl border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300">
                          <span className="text-white text-base font-medium">📚 Has backstory</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* CTA */}
                <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl rounded-2xl border border-white/20 p-8 shadow-2xl">
                  <div className="text-center space-y-4">
                    <h3 className="text-2xl font-bold text-white">Create Your Own Characters!</h3>
                    <p className="text-gray-300 text-lg">
                      Join Pixley to bring your own characters to life with AI-powered animation.</p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                      <a
                        href="/"
                        className="group px-8 py-4 bg-gradient-to-r from-purple-600 via-purple-700 to-pink-600 hover:from-purple-700 hover:via-purple-800 hover:to-pink-700 text-white rounded-2xl font-semibold transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/40 active:scale-95 transform hover:-translate-y-1 border border-white/10"
                      >
                        <div className="flex items-center justify-center space-x-3">
                          <span>Try Pixley — Create Your Own</span>
                          <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </div>
                      </a>
                      <a
                        href="/"
                        className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-2xl font-semibold transition-all duration-200 hover:shadow-lg border border-white/20"
                      >
                        Learn More
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="relative z-10 bg-gradient-to-br from-purple-900/80 to-pink-900/80 backdrop-blur-xl border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <p className="text-gray-300 text-lg mb-6">
              Want to create characters like this? Download Pixley on the App Store!
            </p>
            <a
              href="https://apps.apple.com/us/app/pixley-personalized-cartoons/id6751121342"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <img 
                src="/assets/download_on_app_store.svg" 
                alt="Download on the App Store" 
                className="h-12 hover:scale-105 transition-transform duration-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareCharacter;
